import React, { useState, useEffect, useRef } from 'react';
import Footer from '../components/Footer';
import './Chaplet.css';

// ---- Shared prayer texts ----
const GLORY_BE = `Glory be to the Father, and to the Son, and to the Holy Ghost,
as it was in the beginning, is now, and ever shall be,
world without end. Amen.`;

const ARISE = `Arise, O Lord, and let Thy enemies be scattered,
* and let all that hate Thee flee from before Thy Face.`;

const OPENING = `O God, come to my assistance;
* O Lord, make haste to help me.`;

const MEDAL = `O God, our Protector, look down upon us,
and cast Thine eyes upon the Face of Thy Christ.`;

const OPTIONAL = [
  `Eternal Father, I offer Thee the Cross of our Lord Jesus Christ and all the instruments of His Holy Passion, that Thou may put division in the camp of Thine enemies; for as Thy beloved Son has said, "A kingdom divided against itself shall fall."`,
  `May the thrice Holy Name of God overthrow all their plans.`,
  `May the Name of the Living God split them up by disagreements.`,
  `May the terrible Name of God of Eternity stamp out all their godlessness!`,
  `Lord, I do not desire the death of the sinner, but I want him converted and to live. "Father, forgive them, for they know not what they do."`,
  `May the most holy, most sacred, most adorable, most incomprehensible and unutterable Name of God be always praised, blessed, loved, adored and glorified in Heaven, on earth, and in the hells, by all the creatures of God, and by the Sacred Heart of our Lord Jesus Christ in the Most Holy Sacrament of the Altar. Amen.`,
];

const DECADES = [
  { label: 'First Decade',  sense: 'Sense of Touch',                intention: 'In honor of the sense of touch',                   beads: 6 },
  { label: 'Second Decade', sense: 'Sense of Hearing',             intention: 'In honor of the sense of hearing',                 beads: 6 },
  { label: 'Third Decade',  sense: 'Sense of Sight',               intention: 'In honor of the sense of sight',                   beads: 6 },
  { label: 'Fourth Decade', sense: 'Sense of Smell',               intention: 'In honor of the sense of smell',                   beads: 6 },
  { label: 'Fifth Decade',  sense: 'Sense of Taste',               intention: 'In honor of the sense of taste',                   beads: 6 },
  { label: 'Sixth Decade',  sense: 'Three Years of Public Life',   intention: "In honor of Our Lord's three years of public life", beads: 3 },
];

// ---- Build the flat sequence of prayer screens ----
function buildScreens() {
  const s = [];
  s.push({ id: 'start', kind: 'start' });
  s.push({ id: 'opening', kind: 'opening', section: 'Opening · The Crucifix' });
  DECADES.forEach((d, di) => {
    s.push({ id: `d${di}-large`, kind: 'large', decade: di, beadPos: 0, section: `${d.label} · ${d.sense}` });
    for (let i = 1; i <= d.beads; i++) {
      s.push({ id: `d${di}-s${i}`, kind: 'small', decade: di, beadPos: i, smallIndex: i, section: `${d.label} · ${d.sense}` });
    }
  });
  s.push({ id: 'medal', kind: 'medal', section: 'Conclusion · Holy Face Medal' });
  OPTIONAL.forEach((_, i) => s.push({ id: `opt${i}`, kind: 'optional', optIndex: i, section: 'Recommended · Optional Prayers' }));
  s.push({ id: 'complete', kind: 'complete' });
  return s;
}

const SCREENS = buildScreens();

// Segments for the jump rail
const JUMP = [
  { label: 'Crucifix', id: 'opening' },
  ...DECADES.map((d, di) => ({ label: d.sense.replace('Sense of ', '').replace('Three Years of Public Life', 'Public Life'), id: `d${di}-large` })),
  { label: 'Medal', id: 'medal' },
  { label: 'Optional', id: 'opt0' },
];

// Render multiline prayer text; the response half (after *) gets its own style.
function PrayerLines({ text }) {
  const lines = text.split('\n');
  return (
    <>
      {lines.map((line, i) => {
        const isResponse = line.trimStart().startsWith('*');
        return (
          <React.Fragment key={i}>
            <span className={isResponse ? 'prayer-response' : undefined}>{line || ' '}</span>
            {i < lines.length - 1 && <br />}
          </React.Fragment>
        );
      })}
    </>
  );
}

function BeadStrip({ decade, beadPos }) {
  const d = DECADES[decade];
  return (
    <div className="bead-strip" aria-hidden="true">
      <span className={`bs-bead bs-bead--large ${beadPos === 0 ? 'bs-bead--active' : 'bs-bead--done'}`} />
      {Array.from({ length: d.beads }).map((_, i) => {
        const pos = i + 1;
        const cls = pos === beadPos ? 'bs-bead--active' : pos < beadPos ? 'bs-bead--done' : '';
        return <span key={i} className={`bs-bead ${cls}`} />;
      })}
    </div>
  );
}

export default function Chaplet() {
  const [current, setCurrent] = useState(0);
  const touchX = useRef(null);

  const screen = SCREENS[current];
  const isStart = screen.kind === 'start';
  const isComplete = screen.kind === 'complete';

  const go = (dir) => setCurrent((c) => Math.min(SCREENS.length - 1, Math.max(0, c + dir)));
  const jumpTo = (id) => {
    const idx = SCREENS.findIndex((s) => s.id === id);
    if (idx >= 0) setCurrent(idx);
  };

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setCurrent((c) => Math.min(SCREENS.length - 1, c + 1));
      if (e.key === 'ArrowLeft') setCurrent((c) => Math.max(0, c - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Swipe navigation
  const onTouchStart = (e) => { touchX.current = e.changedTouches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx > 45) go(-1);
    else if (dx < -45) go(1);
    touchX.current = null;
  };

  const progress = Math.round((current / (SCREENS.length - 1)) * 100);

  // Bottom counter text
  let counter = '';
  if (screen.kind === 'small') counter = `Bead ${screen.smallIndex} of ${DECADES[screen.decade].beads}`;
  else if (screen.kind === 'large') counter = 'Large bead';
  else if (screen.kind === 'opening') counter = 'Sign of the Cross';
  else if (screen.kind === 'medal') counter = 'Concluding prayer';
  else if (screen.kind === 'optional') counter = `Optional prayer ${screen.optIndex + 1} of ${OPTIONAL.length}`;

  // ---- Start screen ----
  if (isStart) {
    return (
      <div className="page page-dark chaplet-viewer">
        <div className="cv-start">
          <div className="cv-start__glow" />
          <span className="section-label light">Devotional Prayer</span>
          <h1 className="cv-start__title">Chaplet of the<br />Holy Face</h1>
          <p className="cv-start__sub">A crown of reparation and love</p>
          <p className="cv-start__intro">
            Prayed on a Crucifix, a medal of the Holy Face, and thirty-nine beads, to honor the
            Five Senses of our Lord and make reparation for the outrages against His Adorable Face.
            The Glory Be is said seven times throughout, for the Seven Sorrows of Our Lady and the
            Seven Last Words of Christ.
          </p>
          <button className="cv-begin" onClick={() => setCurrent(1)}>Begin</button>
          <p className="cv-start__hint">Use ← → keys, swipe, or the arrows to move bead by bead.</p>
        </div>
        <Footer />
      </div>
    );
  }

  // ---- Completion screen ----
  if (isComplete) {
    return (
      <div className="page page-dark chaplet-viewer">
        <div className="cv-stage cv-stage--complete">
          <h2 className="cv-complete__title">Chaplet Complete</h2>
          <p className="cv-complete__verse">
            "Look to him and be radiant, so your faces shall never be ashamed."
          </p>
          <cite className="cv-complete__cite">— Psalm 34:5</cite>
          <div className="cv-complete__actions">
            <button className="cv-begin" onClick={() => setCurrent(1)}>Begin Again</button>
            <button className="cv-textbtn" onClick={() => go(-1)}>← Back</button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ---- Prayer screens ----
  return (
    <div className="page page-dark chaplet-viewer">
      <div className="cv">
        <div className="cv-top">
          <span className="cv-section">{screen.section}</span>
          {screen.decade != null && <BeadStrip decade={screen.decade} beadPos={screen.beadPos} />}
        </div>

        <div
          className={`cv-stage cv-stage--${screen.kind}`}
          key={screen.id}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {screen.kind === 'opening' && (
            <>
              <p className="cv-instruction">On the Crucifix, make the Sign of the Cross and pray:</p>
              <p className="cv-latin">Deus in adjutorium meum intende; Domine ad adjuvandum me festina.</p>
              <p className="cv-prayer"><PrayerLines text={OPENING} /></p>
              <p className="cv-prayer cv-prayer--sub"><PrayerLines text={GLORY_BE} /></p>
            </>
          )}

          {screen.kind === 'large' && (
            <>
              <p className="cv-prayer cv-prayer--accent">My Jesus, Mercy.</p>
              <p className="cv-intention">{DECADES[screen.decade].intention}</p>
              <p className="cv-prayer cv-prayer--sub"><PrayerLines text={GLORY_BE} /></p>
            </>
          )}

          {screen.kind === 'small' && (
            <p className="cv-prayer"><PrayerLines text={ARISE} /></p>
          )}

          {screen.kind === 'medal' && (
            <>
              <p className="cv-instruction">On the Holy Face medal, conclude the Chaplet:</p>
              <p className="cv-prayer"><PrayerLines text={MEDAL} /></p>
            </>
          )}

          {screen.kind === 'optional' && (
            <>
              <p className="cv-optlabel">Optional Prayer {screen.optIndex + 1}</p>
              <p className="cv-prayer cv-prayer--optional">{OPTIONAL[screen.optIndex]}</p>
            </>
          )}
        </div>

        <div className="cv-controls">
          <button className="cv-arrow" onClick={() => go(-1)} aria-label="Previous">←</button>
          <span className="cv-counter">{counter}</span>
          <button className="cv-arrow cv-arrow--next" onClick={() => go(1)} aria-label="Next">→</button>
        </div>

        <div className="cv-progress"><div className="cv-progress__fill" style={{ width: `${progress}%` }} /></div>

        <div className="cv-jump">
          {JUMP.map((j) => {
            const active = screen.section && SCREENS.find((s) => s.id === j.id)?.section === screen.section;
            return (
              <button
                key={j.id}
                className={`cv-jump__btn ${active ? 'cv-jump__btn--active' : ''}`}
                onClick={() => jumpTo(j.id)}
              >
                {j.label}
              </button>
            );
          })}
        </div>
      </div>
      <Footer />
    </div>
  );
}
